import {
  IconButton,
  Box,
  FormikSelectField,
  FormikTextField,
  Spacer,
  Title,
  SelectChangeEvent,
} from '@color/continuum';
import FilterAltOffOutlinedIcon from '@mui/icons-material/FilterAltOffOutlined';
import { useFormikContext } from 'formik';
import { ReactNode } from 'react';

import { IVariantClassificationListParams, VariantClassificationState } from '../../types';
import {
  DEFAULT_VARIANT_CLASSIFICATION_SEARCH_PARAMS,
  VariantClassificationStateAllOption,
  VariantClassificationClinicalDomain,
  VariantClassificationVariants,
  VariantClassificationReadyForReview,
} from '../constants';

import { VariantEffectFields } from './VariantEffectFields';

const SEARCH_FIELD_WIDTH = '220px';
const FILTER_FIELD_WIDTH = '180px';

interface FilterFieldProps {
  name: string;
  label: string;
  options: Array<{ value: string; label: ReactNode }>;
  disabled?: boolean;
  onChange?: (event: SelectChangeEvent<unknown>) => void;
  includeEmptyOption?: boolean;
}

const FilterField: React.FC<FilterFieldProps> = ({
  name,
  label,
  options,
  disabled,
  onChange,
  includeEmptyOption = false,
}) => (
  <Box sx={{ width: FILTER_FIELD_WIDTH }}>
    <FormikSelectField
      name={name}
      label={label}
      options={options}
      optionalIndicatorIsEnabled={false}
      disabled={disabled}
      {...(onChange && { onChange })}
      includeEmptyOption={includeEmptyOption}
    />
  </Box>
);

export const VariantClassificationFormFields = () => {
  const { values, resetForm, submitForm, setFieldValue } =
    useFormikContext<IVariantClassificationListParams>();

  const doShowClassificationFilter =
    values.state === VariantClassificationState.INTERPRETED ||
    values.state === VariantClassificationState.REREVIEW ||
    values.state === VariantClassificationState.REVIEWED;

  const handleReadyForReviewChange = (event: SelectChangeEvent<unknown>) => {
    const value = event.target.value as string;
    setFieldValue('readyForReview', value);
    if (value !== '') {
      setFieldValue('state', VariantClassificationState.REVIEWED);
      setFieldValue('variants', VariantClassificationVariants.IN_REPORTS);
    }

    if (value === VariantClassificationReadyForReview.EXTERNAL) {
      setFieldValue('clinicalDomain', VariantClassificationClinicalDomain.CANCER);
    }
  };

  return (
    <>
      <Box>
        <Title variant="headline3">Filter by</Title>
        <Spacer variant="xSmall" />
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 1,
          }}
        >
          <FilterField
            name="state"
            label="State"
            options={[
              { value: VariantClassificationState.NEW, label: 'New' },
              { value: VariantClassificationState.REREVIEW, label: 'Rereview' },
              { value: VariantClassificationState.IN_PROGRESS, label: 'In Progress' },
              { value: VariantClassificationState.INTERPRETED, label: 'Reviewable' },
              { value: VariantClassificationState.REVIEWED, label: 'Classified' },
              { value: VariantClassificationStateAllOption, label: 'All' },
            ]}
            disabled={Boolean(values.readyForReview !== '')}
          />
          <FilterField
            name="clinicalDomain"
            label="Clinical Domain"
            options={[
              { value: VariantClassificationClinicalDomain.ALL, label: 'All' },
              { value: VariantClassificationClinicalDomain.CANCER, label: 'Cancer' },
              { value: VariantClassificationClinicalDomain.CARDIO, label: 'Cardio' },
              { value: VariantClassificationClinicalDomain.OTHER, label: 'Other' },
            ]}
            disabled={Boolean(
              values.readyForReview === VariantClassificationReadyForReview.EXTERNAL
            )}
          />
          <FilterField
            name="variants"
            label="Variants"
            options={[
              { value: VariantClassificationVariants.ALL, label: 'All' },
              { value: VariantClassificationVariants.CALLED, label: 'Called' },
              { value: VariantClassificationVariants.IN_REPORTS, label: 'In Reports' },
            ]}
          />
          <FilterField
            name="readyForReview"
            label="Ready for review"
            options={[
              { value: VariantClassificationReadyForReview.INTERNAL, label: 'Internal' },
              { value: VariantClassificationReadyForReview.EXTERNAL, label: 'External' },
            ]}
            onChange={handleReadyForReviewChange}
            includeEmptyOption
          />
          <VariantEffectFields />
        </Box>
      </Box>
      <Spacer variant="xSmall" />
      <Box>
        <Title variant="headline3">Search by</Title>
        <Spacer variant="xSmall" />
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 1,
          }}
        >
          <FormikTextField
            name="kitBarcode"
            label="Kit Barcode"
            optionalIndicatorIsEnabled={false}
            sx={{ width: SEARCH_FIELD_WIDTH }}
          />
          <FormikTextField
            name="batchId"
            label="Batch ID"
            optionalIndicatorIsEnabled={false}
            sx={{ width: SEARCH_FIELD_WIDTH }}
          />
          <FormikTextField
            name="nomenclature"
            label="Nomenclature"
            optionalIndicatorIsEnabled={false}
            sx={{ width: SEARCH_FIELD_WIDTH }}
          />
          <FormikTextField
            name="gene"
            label="Gene"
            optionalIndicatorIsEnabled={false}
            sx={{ width: SEARCH_FIELD_WIDTH }}
          />
          <FormikTextField
            name="exon"
            label="Exon"
            optionalIndicatorIsEnabled={false}
            sx={{ width: SEARCH_FIELD_WIDTH }}
          />
          {doShowClassificationFilter && (
            <FormikTextField
              name="classification"
              label="Classification"
              optionalIndicatorIsEnabled={false}
              sx={{ width: SEARCH_FIELD_WIDTH }}
            />
          )}
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton
              aria-label="Clear filters"
              onClick={() => {
                resetForm({ values: DEFAULT_VARIANT_CLASSIFICATION_SEARCH_PARAMS });
                submitForm();
              }}
            >
              <FilterAltOffOutlinedIcon />
            </IconButton>
          </Box>
        </Box>
      </Box>
    </>
  );
};
